import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Paper,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import EventTrendChart, { isEventTrendData } from "../charts/EventTrendChart";
import {
  AnswerBlock,
  AssistantItem,
  ChatItem,
  Env,
  EventTrendRow,
  GraphDefinition,
  PatchEdit,
} from "./utils/types";
import {
  ACCENT,
  BROWN,
  COBALT,
  EMAIL_API_KEY,
  EXAMPLES,
  pulseKeyframes,
  buildApiUrls,
} from "./utils/constants";
import {
  coerceLike,
  dedupe,
  formatCodeContent,
  formatJson,
} from "./utils/formatter";
import {
  clearThreadId,
  loadEnv,
  loadMessages,
  loadThreadId,
  saveEnv,
  saveMessages,
} from "./utils/storage";
import {
  coerceEnvelope,
  describeImplication,
  graphDefToEventRows,
  hydratePatchForRender,
  isGraphDefinitionLike,
  normalizeToChatItem,
} from "./utils/chat";
import Bubble from "../chat/Bubble";
import TypingDots from "../chat/TypingDots";
import { DRAWER_WIDTH_LG, DRAWER_WIDTH_MD } from "../../App";

export default function FeatureConfiguratorV1() {
  const [env, setEnv] = useState<Env>(loadEnv() ?? "dev");
  const { askUrl, patchUrl, immediateRcUrl } = useMemo(
    () => buildApiUrls(env),
    [env],
  );
  const [defaultInputs, setDefaultInputs] = useState<Record<number, string>>(
    {},
  );
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeImmediatePatch, setActiveImmediatePatch] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState<any | null>(null);
  const [messages, setMessages] = useState<ChatItem[]>(() => loadMessages());
  const [editInputs, setEditInputs] = useState<
    Record<number, Record<string, string>>
  >({});
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const typingIndexRef = useRef<number | null>(null);
  const threadIdRef = useRef<string>(loadThreadId());
  const endOfMessagesRef = useRef<HTMLDivElement | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmIdx, setConfirmIdx] = useState<number | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<
    Record<number, { ok: boolean; msg: string }>
  >({});

  const canRun = useMemo(
    () => prompt.trim().length > 0 && !loading,
    [prompt, loading],
  );

  useEffect(() => saveMessages(messages), [messages]);
  useEffect(() => saveEnv(env), [env]);

  useEffect(() => {
    if (messages.length <= 1) return;
    const scrollToBottom = () => {
      if (!endOfMessagesRef.current) return;
      const composerHeight = 120;
      window.scrollTo({
        top: document.body.scrollHeight - composerHeight,
        behavior: "smooth",
      });
    };

    const id = setTimeout(scrollToBottom, 150);
    return () => clearTimeout(id);
  }, [messages, loading]);

  const showFeedback = (message: string) => {
    setCopyFeedback(message);
    setTimeout(() => setCopyFeedback(null), 3000);
  };

  const extractMessageText = (msg: ChatItem): string => {
    if (msg.role === "user") {
      return msg.content;
    }

    const a = msg as AssistantItem;
    let text = "";

    if (a.header) text += `${a.header}\n\n`;
    if (a.prose) text += `${a.prose}\n`;

    if (a.chips?.length) {
      text += `\nTags: ${a.chips.join(", ")}\n`;
    }

    if (a.blocks?.length) {
      a.blocks.forEach((block: AnswerBlock) => {
        if (block.type === "code") {
          text += `\n--- ${block.label || "Code"} ---\n`;
          text += `${formatCodeContent(block.content)}\n`;
        } else if (block.type === "raw") {
          text += `\n--- ${block.label || "Data"} ---\n`;
          text += `${JSON.stringify(block.content, null, 2)}\n`;
        }
      });
    }

    return text;
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(
        () => true,
        () => false,
      );
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        textArea.remove();
        return true;
      } catch (error) {
        textArea.remove();
        return false;
      }
    }
  };

  const handleCopyMessage = useCallback(async (msg: ChatItem) => {
    const text = extractMessageText(msg);
    const success = await copyToClipboard(text);
    showFeedback(
      success ? "✅ Message copied to clipboard!" : "❌ Failed to copy message",
    );
  }, []);

  const onRun = useCallback(async () => {
    if (!canRun) return;
    const userMsg: ChatItem = { role: "user", content: prompt.trim() };
    setMessages(m => {
      const typing: ChatItem = { role: "assistant", isTyping: true };
      const next = [...m, userMsg, typing];
      typingIndexRef.current = next.length - 1;
      return next;
    });
    setPrompt("");
    setLoading(true);

    try {
      const res = await axiosInstance.post(
        askUrl,
        {
          prompt: userMsg.content,
          threadId: threadIdRef.current,
          history: messages.map(m =>
            m.role === "user"
              ? { role: "user", content: m.content }
              : {
                  role: "assistant",
                  content: (m as any).prose || (m as any).header || "",
                  matchedParameter: (m as any).matchedParameter,
                },
          ),
        },
        {
          headers: {
            emailApiKey: EMAIL_API_KEY,
            "Content-Type": "application/json",
          },
        },
      );
      const data = coerceEnvelope(res?.data);
      if (!data) {
        replaceTypingWith({
          role: "assistant",
          isError: true,
          prose: "Empty response from server.",
        });
        return;
      }
      const normalized = normalizeToChatItem(data);
      replaceTypingWith(normalized);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Request failed. Please try again.";
      replaceTypingWith({
        role: "assistant",
        isError: true,
        prose: msg,
        raw: e?.response?.data,
      });
    } finally {
      setLoading(false);
    }
  }, [canRun, messages, prompt, askUrl]);

  const handleReRequest = useCallback(
    (messageIndex: number) => {
      if (messageIndex === 0) return;

      const userMessageIndex = messageIndex - 1;
      const userMsg = messages[userMessageIndex];

      if (userMsg.role !== "user") return;

      setMessages(m => m.slice(0, messageIndex));

      setPrompt(userMsg.content);
      setTimeout(() => {
        onRun();
      }, 100);
    },
    [messages, onRun],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const metaOrCtrl = e.metaKey || e.ctrlKey;
      if (metaOrCtrl && e.key.toLowerCase() === "enter") {
        e.preventDefault();
        if (canRun) void onRun();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canRun, onRun]);

  function replaceTypingWith(item: AssistantItem) {
    setMessages(m => {
      const next = [...m];
      if (typingIndexRef.current != null) {
        next[typingIndexRef.current] = item;
        typingIndexRef.current = null;
      } else {
        next.push(item);
      }
      return next;
    });
  }

  const onUseExample = (text: string) => {
    setPrompt(text);
    setTimeout(() => editorRef.current?.focus(), 0);
  };

  const onClearChat = () => {
    setMessages([]);

    // Reset conversation thread
    clearThreadId();
    threadIdRef.current = loadThreadId();
  };

  const COMPOSER_H = 78;

  function buildPublishBody(index: number) {
    const msg = messages[index] as AssistantItem;
    const patch = msg.patch;
    if (!patch) throw new Error("No patch for this message.");

    let body: any;

    if (patch.previewCode) {
      const parsed = JSON.parse(patch.previewCode);
      body = {
        paramName: parsed.parameter ?? patch.parameter,
        comment: parsed.comment || patch.comment || `Patch ${patch.parameter}`,
        ...(Object.prototype.hasOwnProperty.call(parsed, "default")
          ? { default: parsed.default }
          : { edits: Array.isArray(parsed.edits) ? parsed.edits : [] }),
      };
    } else {
      const hasDefaultInline = Object.prototype.hasOwnProperty.call(
        patch as any,
        "default",
      );
      body = {
        paramName: patch.parameter,
        comment: patch.comment || `Patch ${patch.parameter}`,
        ...(hasDefaultInline
          ? { default: (patch as any).default }
          : { edits: Array.isArray(patch.edits) ? patch.edits : [] }),
      };
    }

    const isDefaultCase = Object.prototype.hasOwnProperty.call(body, "default");

    if (isDefaultCase) {
      const currentSample = body.default;
      const overrideRaw = (defaultInputs[index] ?? "").trim();
      if (overrideRaw.length > 0) {
        body.default = coerceLike(currentSample, overrideRaw);
      }
      delete body.edits;
    } else {
      if (Array.isArray(body.edits) && body.edits.length > 0) {
        body.edits = applyConditionOverrides(index, body.edits);
      } else {
        const overrideRaw = (defaultInputs[index] ?? "").trim();
        if (overrideRaw.length > 0) {
          body.default = coerceLike("", overrideRaw);
          delete body.edits;
        }
      }
    }

    if (
      !Object.prototype.hasOwnProperty.call(body, "default") &&
      (!Array.isArray(body.edits) || body.edits.length === 0)
    ) {
      const overrideRaw = (defaultInputs[index] ?? "").trim();
      if (overrideRaw.length > 0) {
        body.default = coerceLike("", overrideRaw);
        delete body.edits;
      } else if (Array.isArray(body.edits) && body.edits.length > 0) {
        body.edits = applyConditionOverrides(index, body.edits);
      }
    }

    const hasDefaultFinal = Object.prototype.hasOwnProperty.call(
      body,
      "default",
    );
    const hasEditsFinal = Array.isArray(body.edits) && body.edits.length > 0;

    if (
      (hasDefaultFinal && hasEditsFinal) ||
      (!hasDefaultFinal && !hasEditsFinal)
    ) {
      throw new Error(
        'Invalid patch payload: provide either "default" OR a non-empty "edits" array.',
      );
    }

    return body;
  }

  function openConfirm(idx: number) {
    setConfirmIdx(idx);

    const msg = messages[idx] as any;
    const immediatePatch = msg.immediateRcPatch;

    if (immediatePatch) {
      setActiveImmediatePatch(true);
      setConfirmPreview(immediatePatch);
    } else {
      try {
        const body = buildPublishBody(idx);
        setActiveImmediatePatch(false);
        setConfirmPreview(body);
      } catch (err) {
        console.error("Failed to build publish body for confirm dialog", err);
        setActiveImmediatePatch(false);
        setConfirmPreview(null);
      }
    }

    setConfirmOpen(true);
  }

  function closeConfirm() {
    setConfirmOpen(false);
    setConfirmIdx(null);
    setConfirmPreview(null);
    setActiveImmediatePatch(false);
  }

  async function confirmApply() {
    if (confirmIdx == null) return;
    setApplying(true);

    try {
      const msg = messages[confirmIdx] as any;

      if (msg.immediateRcPatch) {
        const body = msg.immediateRcPatch;

        await axiosInstance.post(immediateRcUrl, body, {
          headers: {
            emailApiKey: EMAIL_API_KEY,
            "Content-Type": "application/json",
          },
        });

        setApplyResult(s => ({
          ...s,
          [confirmIdx]: { ok: true, msg: "Immediate RC updated successfully." },
        }));
      } else {
        const body = buildPublishBody(confirmIdx);

        await axiosInstance.post(patchUrl, body, {
          headers: {
            emailApiKey: EMAIL_API_KEY,
            "Content-Type": "application/json",
          },
        });

        setApplyResult(s => ({
          ...s,
          [confirmIdx]: {
            ok: true,
            msg: "Remote Config updated successfully.",
          },
        }));
      }
    } catch (e: any) {
      const emsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to publish patch.";

      setApplyResult(s => ({
        ...s,
        [confirmIdx]: { ok: false, msg: emsg },
      }));
    } finally {
      setApplying(false);
      closeConfirm();
    }
  }

  function applyConditionOverrides(
    index: number,
    baseEdits: PatchEdit[],
  ): PatchEdit[] {
    const overrides = editInputs[index] || {};
    if (!baseEdits?.length) return baseEdits;

    return baseEdits.map(e => {
      const raw = overrides[e.condition];
      if (typeof raw === "undefined") return e;
      const coerced = coerceLike(e.value as any, raw);
      return { ...e, value: coerced };
    });
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        overflowX: "hidden",
        display: "flex",
        justifyContent: "center",
        maxWidth: "100vw",
        boxSizing: "border-box",
        bgcolor: "#fff",
        backgroundImage: `
          radial-gradient(1200px 600px at 95% 105%, ${alpha(
            ACCENT,
            0.08,
          )} 0%, rgba(255,255,255,0) 60%),
          radial-gradient(900px 500px at -10% -10%, ${alpha(
            COBALT,
            0.06,
          )} 0%, rgba(255,255,255,0) 55%),
          linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,1)),
          linear-gradient(0deg, transparent 24%, ${alpha(
            "#000",
            0.04,
          )} 25%, ${alpha(
            "#000",
            0.04,
          )} 26%, transparent 27%, transparent 74%, ${alpha(
            "#000",
            0.04,
          )} 75%, ${alpha("#000", 0.04)} 76%, transparent 77%),
          linear-gradient(90deg, transparent 24%, ${alpha(
            "#000",
            0.04,
          )} 25%, ${alpha(
            "#000",
            0.04,
          )} 26%, transparent 27%, transparent 74%, ${alpha(
            "#000",
            0.04,
          )} 75%, ${alpha("#000", 0.04)} 76%, transparent 77%)
        `,
        backgroundSize: "auto, auto, auto, 28px 28px, 28px 28px",
        backgroundBlendMode: "screen, screen, normal, multiply, multiply",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: { xs: "100%", md: 1200 },
          mx: "auto",
          px: { xs: 1.5, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: `${COMPOSER_H + 80}px`,
        }}
      >
        {/* Top-right controls */}
        <Box
          sx={{
            position: "fixed",
            top: 80,
            right: 16,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <ToggleButtonGroup
            value={env}
            exclusive
            onChange={(_, val: Env | null) => val && setEnv(val)}
            size="small"
            color="primary"
            sx={{
              bgcolor: "rgba(255,255,255,0.9)",
              borderRadius: 1,
              border: `1px solid ${alpha("#000", 0.08)}`,
              boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
              overflow: "hidden",
              maxWidth: "100%",
              "& .MuiToggleButton-root": {
                color: "text.secondary",
                border: "none",
                fontSize: 11,
                px: 1.5,
                py: 0.5,
                textTransform: "uppercase",
                position: "relative",
                "&.Mui-selected": {
                  color: "#000",
                  fontWeight: 700,
                },
                "&.Mui-selected::after": {
                  content: '""',
                  position: "absolute",
                  left: 8,
                  right: 8,
                  bottom: 3,
                  height: 2,
                  background: `linear-gradient(90deg, ${ACCENT}, ${COBALT})`,
                  borderRadius: 2,
                },
                "&:hover": { bgcolor: alpha(ACCENT, 0.06) },
              },
            }}
          >
            <ToggleButton value="dev">DEV</ToggleButton>
            <ToggleButton value="staging">STAGING</ToggleButton>
            <ToggleButton value="prod">PROD</ToggleButton>
          </ToggleButtonGroup>

          {messages.length > 0 && (
            <Tooltip title="Clear chat">
              <IconButton
                size="small"
                color="error"
                onClick={onClearChat}
                sx={{
                  mt: 1,
                  bgcolor: "rgba(255,255,255,0.95)",
                  borderRadius: 1.5,
                  border: `1px solid ${alpha("#000", 0.08)}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,1)",
                  },
                }}
              >
                <DeleteSweepIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Header + Transcript container (centered) */}
        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
          <Box sx={{ maxWidth: 980, mx: "auto" }}>
            {loading && <LinearProgress sx={{ borderRadius: 10, mb: 1 }} />}

            {messages.length === 0 ? (
              <Stack
                spacing={1.25}
                alignItems="center"
                textAlign="center"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: "-0.02em",
                    mt: 2,
                    fontSize: { xs: 28, sm: 34, md: 42, lg: 50 },
                    color: BROWN,
                  }}
                >
                  Hello Admin, Welcome to <br />
                  Kin-AI
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 560 }}
                >
                  Describe your changes and we'll query Remote Config and apply
                  updates safely.
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  flexWrap="wrap"
                  sx={{
                    mt: 1,
                    gap: 1,
                  }}
                >
                  {EXAMPLES.map(ex => (
                    <Chip
                      key={ex}
                      label={ex}
                      onClick={() => onUseExample(ex)}
                      variant="outlined"
                      size="small"
                      sx={{
                        bgcolor: "#fff",
                        borderColor: alpha(ACCENT, 0.35),
                        color: BROWN,
                        "&:hover": { bgcolor: alpha(ACCENT, 0.06) },
                      }}
                    />
                  ))}
                </Stack>

                <Tooltip title="Enter to send. Shift+Enter for newline. Cmd/Ctrl+Enter also works.">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Enter to send • Shift+Enter = newline
                  </Typography>
                </Tooltip>
              </Stack>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
                spacing={1}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ opacity: 0.7, letterSpacing: 0.3 }}
                >
                  Kinship-GPT
                </Typography>
              </Stack>
            )}

            {/* Transcript */}
            <Stack
              spacing={2}
              sx={{
                mb: 1,
                overflowX: "hidden",
              }}
            >
              {messages.map((m, idx) => {
                const immediatePatch = (m as any).immediateRcPatch;
                const isImmediate = !!immediatePatch;
                const isUser = m.role === "user";
                const a = !isUser ? (m as AssistantItem) : undefined;
                const isTyping = !!a?.isTyping;
                const warning = !!a && a.severity === "warning";
                const error = !!a && (a.severity === "error" || a.isError);
                const matched = a?.matchedParameter;
                const patch = a?.patch;

                const hydrated = hydratePatchForRender(
                  patch,
                  (m as any).blocks,
                );
                const wantsApply = hydrated.apply || patch?.apply === true;

                const hasContent =
                  hydrated.hasDefault || hydrated.edits.length > 0;
                const showUpdateCard = !!patch && (wantsApply || hasContent);
                const needsUserDefault =
                  !isImmediate &&
                  !!patch &&
                  wantsApply &&
                  !hydrated.hasDefault &&
                  hydrated.edits.length === 0;

                const hasUserDefault =
                  needsUserDefault &&
                  typeof defaultInputs[idx] === "string" &&
                  defaultInputs[idx].trim().length > 0;

                const needsManualDefault =
                  !isImmediate &&
                  !hydrated.hasDefault &&
                  hydrated.edits.length === 0;

                const result = applyResult[idx];

                return (
                  <Stack
                    key={idx}
                    direction="row"
                    justifyContent={isUser ? "flex-end" : "flex-start"}
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Bubble
                      isUser={isUser}
                      label={
                        isUser
                          ? "You"
                          : matched
                            ? `Kin-AI · ${matched}`
                            : "Kin-AI"
                      }
                      warning={warning}
                      error={error}
                      onCopy={async () => handleCopyMessage(m)}
                      onReRequest={
                        !isUser && !isTyping
                          ? () => handleReRequest(idx)
                          : undefined
                      }
                      showReRequest={!isUser && !isTyping && idx > 0}
                    >
                      {isUser ? (
                        <Typography
                          sx={{
                            whiteSpace: "pre-wrap",
                            color: "#fff",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {(m as any).content}
                        </Typography>
                      ) : isTyping ? (
                        <TypingDots />
                      ) : (
                        <>
                          {(m as any).header && (
                            <Typography
                              sx={{
                                fontWeight: 800,
                                mb: 0.5,
                                letterSpacing: 0.2,
                              }}
                            >
                              {(m as any).header}
                            </Typography>
                          )}

                          {(m as any).prose && (
                            <Typography
                              sx={{
                                whiteSpace: "pre-wrap",
                                mb: (m as any).chips?.length ? 0.75 : 0,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {(m as any).prose}
                            </Typography>
                          )}

                          {(m as any).chips?.length ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              {(m as any).chips.map((chip: string) => (
                                <Chip
                                  key={chip}
                                  label={chip}
                                  size="small"
                                  sx={{
                                    bgcolor: "#fff",
                                    border: `1px solid ${alpha(ACCENT, 0.35)}`,
                                    color: BROWN,
                                  }}
                                />
                              ))}
                            </Box>
                          ) : null}

                          {(m as any).blocks?.map(
                            (b: AnswerBlock, i: number) => {
                              if (b.type === "code") {
                                return (
                                  <Box key={i} sx={{ mt: 1 }}>
                                    <Divider sx={{ my: 1, opacity: 0.6 }} />
                                    {b.label ? (
                                      <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.7 }}
                                      >
                                        {b.label}
                                      </Typography>
                                    ) : null}
                                    <Box
                                      component="pre"
                                      sx={{
                                        m: 0,
                                        mt: 1,
                                        p: 2,
                                        borderRadius: 1,
                                        overflowX: "auto",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        fontFamily:
                                          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                        fontSize: 13,
                                        lineHeight: 1.55,
                                        color: "#111",
                                        backgroundImage: `
                          linear-gradient(0deg, ${alpha(
                            "#000",
                            0.035,
                          )} 1px, transparent 1px),
                          linear-gradient(90deg, ${alpha(
                            "#000",
                            0.035,
                          )} 1px, transparent 1px)
                        `,
                                        backgroundSize: "18px 18px, 18px 18px",
                                        bgcolor: "#fff",
                                        border: `1px solid ${alpha(
                                          "#000",
                                          0.12,
                                        )}`,
                                      }}
                                    >
                                      {formatCodeContent(b.content)}
                                    </Box>
                                  </Box>
                                );
                              }
                              if (b.type === "raw") {
                                const content = (b as any).content;

                                const hasRows = isEventTrendData(content);

                                const graph: GraphDefinition | null =
                                  isGraphDefinitionLike(content) ||
                                  (content &&
                                    typeof content === "object" &&
                                    Array.isArray((content as any).series))
                                    ? (content as GraphDefinition)
                                    : null;

                                let trendData: EventTrendRow[] | null = null;

                                if (hasRows) {
                                  trendData = content as EventTrendRow[];
                                } else if (graph) {
                                  const fromHelper = graphDefToEventRows(graph);

                                  if (
                                    Array.isArray(fromHelper) &&
                                    fromHelper.length > 0
                                  ) {
                                    trendData = fromHelper;
                                  } else if (
                                    (graph as any).chartType === "pie" ||
                                    (graph as any).kind === "pie"
                                  ) {
                                    const series0 = Array.isArray(
                                      (graph as any).series,
                                    )
                                      ? (graph as any).series[0]
                                      : undefined;
                                    const points = (series0 as any)?.points;

                                    if (
                                      Array.isArray(points) &&
                                      points.length > 0
                                    ) {
                                      trendData = points.map((p: any) => ({
                                        event_date: String(p.x),
                                        event_count: Number(p.y ?? 0),
                                      }));
                                    }
                                  }
                                }

                                const initialMode: "line" | "bar" | "pie" =
                                  graph &&
                                  ((graph as any).chartType === "pie" ||
                                    (graph as any).kind === "pie")
                                    ? "pie"
                                    : "line";

                                return (
                                  <Box key={i} sx={{ mt: 1 }}>
                                    <Divider sx={{ my: 1, opacity: 0.6 }} />
                                    <Typography
                                      variant="caption"
                                      sx={{ opacity: 0.7 }}
                                    >
                                      {b.label ||
                                        (trendData ? "Event trend" : "Raw")}
                                    </Typography>

                                    {trendData ? (
                                      <EventTrendChart
                                        data={trendData}
                                        initialMode={initialMode}
                                      />
                                    ) : (
                                      <Box
                                        component="pre"
                                        sx={{
                                          m: 0,
                                          mt: 0.5,
                                          p: 1,
                                          borderRadius: 1,
                                          overflowX: "auto",
                                          fontFamily:
                                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                          fontSize: 13,
                                          bgcolor: "#fff",
                                          border: `1px solid ${alpha(
                                            "#000",
                                            0.12,
                                          )}`,
                                        }}
                                      >
                                        {JSON.stringify(content, null, 2)}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              }

                              return null;
                            },
                          )}

                          {showUpdateCard && patch && (
                            <Paper
                              elevation={0}
                              sx={{
                                mt: 1.25,
                                p: 1.25,
                                borderRadius: 1,
                                border: `1px solid ${alpha("#000", 0.12)}`,
                                bgcolor: alpha(ACCENT, 0.06),
                              }}
                            >
                              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                                {isImmediate
                                  ? "Ready to apply this Immediate RC change?"
                                  : "Ready to publish this change?"}
                              </Typography>

                              {isImmediate ? (
                                (() => {
                                  const immediateTarget =
                                    typeof hydrated.defaultVal !== "undefined"
                                      ? hydrated.defaultVal
                                      : (immediatePatch?.default ??
                                        "[value from Immediate RC patch]");

                                  return (
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                      {`This will update "${
                                        patch.parameter
                                      }" globally to "${String(
                                        immediateTarget,
                                      )}" in Immediate RC. Mobile clients read Immediate RC when the app starts and as users navigate between screens, so this change is picked up almost immediately.`}
                                    </Typography>
                                  );
                                })()
                              ) : (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  {describeImplication(
                                    patch.parameter,
                                    hydrated.edits,
                                    hydrated.defaultVal,
                                  )}
                                </Typography>
                              )}

                              {!isImmediate && (
                                <>
                                  {hydrated.hasDefault ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.8 }}
                                      >
                                        Global default value to publish
                                      </Typography>
                                      <TextField
                                        size="small"
                                        value={
                                          defaultInputs[idx] ??
                                          String(hydrated.defaultVal ?? "")
                                        }
                                        onChange={e =>
                                          setDefaultInputs(s => ({
                                            ...s,
                                            [idx]: e.target.value,
                                          }))
                                        }
                                        placeholder="e.g. 4.17.2"
                                        sx={{
                                          maxWidth: 360,
                                          bgcolor: "#fff",
                                          "& .MuiInputBase-input": {
                                            fontFamily:
                                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                          },
                                        }}
                                        helperText="Prefilled from the suggested patch. You can change it before publishing."
                                      />
                                    </Box>
                                  ) : hydrated.edits.length > 0 ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      {dedupe(
                                        hydrated.edits.map(e => e.condition),
                                      ).map(cond => {
                                        const sample = hydrated.edits.find(
                                          e => e.condition === cond,
                                        )?.value;
                                        const current = editInputs[idx]?.[cond];
                                        const initial =
                                          typeof current === "string"
                                            ? current
                                            : String(sample ?? "");
                                        return (
                                          <Box
                                            key={cond}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <Chip
                                              label={cond}
                                              size="small"
                                              sx={{
                                                borderColor: alpha(
                                                  "#000",
                                                  0.12,
                                                ),
                                              }}
                                            />
                                            <TextField
                                              size="small"
                                              value={initial}
                                              onChange={e =>
                                                setEditInputs(s => ({
                                                  ...s,
                                                  [idx]: {
                                                    ...(s[idx] || {}),
                                                    [cond]: e.target.value,
                                                  },
                                                }))
                                              }
                                              placeholder="Enter value…"
                                              sx={{
                                                maxWidth: 360,
                                                bgcolor: "#fff",
                                                "& .MuiInputBase-input": {
                                                  fontFamily:
                                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                                },
                                              }}
                                            />
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  ) : needsManualDefault ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.8 }}
                                      >
                                        Global default value to publish
                                      </Typography>
                                      <TextField
                                        size="small"
                                        value={defaultInputs[idx] ?? ""}
                                        onChange={e =>
                                          setDefaultInputs(s => ({
                                            ...s,
                                            [idx]: e.target.value,
                                          }))
                                        }
                                        placeholder="e.g. 4.17.2"
                                        sx={{
                                          maxWidth: 360,
                                          bgcolor: "#fff",
                                          "& .MuiInputBase-input": {
                                            fontFamily:
                                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                          },
                                        }}
                                        helperText="No default or condition edits were provided. Enter a value so we can publish."
                                      />
                                    </Box>
                                  ) : null}
                                </>
                              )}

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <Button
                                  variant="contained"
                                  onClick={() => openConfirm(idx)}
                                  disabled={needsUserDefault && !hasUserDefault}
                                  sx={{
                                    backgroundColor: BROWN,
                                    "&:hover": {
                                      backgroundColor: BROWN,
                                    },
                                  }}
                                >
                                  {isImmediate
                                    ? "Apply Immediate RC"
                                    : "Push this update"}
                                </Button>
                                {result ? (
                                  <Typography
                                    variant="body2"
                                    sx={{ opacity: 0.9 }}
                                    color={
                                      result.ok ? "success.main" : "error.main"
                                    }
                                  >
                                    {result.msg}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Paper>
                          )}

                          {patch?.previewCode && !(m as any).blocks?.length ? (
                            <Box sx={{ mt: 1 }}>
                              <Divider sx={{ my: 1, opacity: 0.6 }} />
                              <Typography
                                variant="caption"
                                sx={{ opacity: 0.7 }}
                              >
                                {patch.previewHeader || "Patch"}
                              </Typography>
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  mt: 0.5,
                                  p: 1,
                                  borderRadius: 1,
                                  overflowX: "auto",
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  fontSize: 13,
                                  bgcolor: "#fff",
                                  border: `1px solid ${alpha("#000", 0.12)}`,
                                }}
                              >
                                {patch.previewCode}
                              </Box>
                            </Box>
                          ) : null}

                          {error && (m as any).prose && (
                            <Alert
                              severity="error"
                              variant="outlined"
                              sx={{ mt: 1, borderRadius: 1 }}
                            >
                              {(m as any).prose}
                            </Alert>
                          )}
                          {warning && (m as any).prose && (
                            <Alert
                              severity="warning"
                              variant="outlined"
                              sx={{ mt: 1, borderRadius: 1 }}
                            >
                              {(m as any).prose}
                            </Alert>
                          )}
                        </>
                      )}
                    </Bubble>
                  </Stack>
                );
              })}
            </Stack>

            <div ref={endOfMessagesRef} />
          </Box>
        </Box>

        {/* Composer */}
        <Box
          sx={{
            position: "fixed",
            left: { xs: 0, md: DRAWER_WIDTH_MD, lg: DRAWER_WIDTH_LG },
            right: 0,
            bottom: 0,
            py: 2,
            backdropFilter: "blur(12px)",
            background: "rgba(255,255,255,0.8)",
            zIndex: t => t.zIndex.appBar + 1,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 980,
              mx: "auto",
              px: { xs: 1, sm: 2 },
              boxSizing: "border-box",
              overflowX: "hidden",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 999,
                px: 2,
                py: 1,
                bgcolor: alpha("#F7F7F7", 0.9),
                border: `1px solid ${alpha("#000", 0.1)}`,
                boxShadow: `0 0 0 1px ${alpha(ACCENT, 0.06)}`,
              }}
            >
              <TextField
                inputRef={editorRef}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Describe changes here…"
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                variant="standard"
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (canRun) onRun();
                  }
                }}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    px: 1,
                    py: 0.5,
                    fontSize: { xs: 18, sm: 20 },
                    color: "text.primary",
                    "::placeholder": {
                      opacity: 0.6,
                      color: "text.secondary",
                    },
                  },
                }}
              />

              <Button
                onClick={onRun}
                disabled={!prompt.trim() || loading}
                variant="contained"
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  ml: 2,
                  background: `linear-gradient(180deg, ${ACCENT}, ${COBALT})`,
                  color: "#fff",
                  transition: "transform 160ms ease",
                  ...pulseKeyframes,
                  animation:
                    !prompt.trim() || loading
                      ? "none"
                      : "cyanPulse 2.4s infinite",
                  "&:hover": { transform: "scale(1.05)" },
                  "&.Mui-disabled": {
                    opacity: 0.35,
                    background: alpha("#000", 0.12),
                  },
                }}
              >
                <SendRoundedIcon fontSize="medium" />
              </Button>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Confirm Modal */}
      <Dialog
        open={confirmOpen}
        onClose={applying ? undefined : closeConfirm}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {activeImmediatePatch
            ? "Confirm Immediate RC patch"
            : "Confirm publish"}
        </DialogTitle>
        <DialogContent dividers>
          {confirmIdx != null && confirmPreview ? (
            <>
              <Typography sx={{ mb: 1.5 }}>
                {activeImmediatePatch
                  ? "You are about to apply this Immediate RC patch. It will update the Immediate RC store for the selected environment."
                  : "You're about to publish this Remote Config patch. Are you sure?"}
              </Typography>

              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  borderRadius: 1,
                  overflowX: "auto",
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: 13,
                  bgcolor: "#fff",
                  border: `1px solid ${alpha("#000", 0.12)}`,
                }}
              >
                {formatJson(confirmPreview)}
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={applying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmApply}
            disabled={applying}
            sx={{
              backgroundColor: BROWN,
              "&:hover": { backgroundColor: BROWN },
            }}
          >
            {applying
              ? activeImmediatePatch
                ? "Applying…"
                : "Publishing…"
              : activeImmediatePatch
                ? "Apply Immediate RC"
                : "Publish patch"}
          </Button>
        </DialogActions>
      </Dialog>

      {copyFeedback && (
        <Alert
          severity={copyFeedback.includes("✅") ? "success" : "error"}
          sx={{
            position: "fixed",
            top: 88,
            right: 16,
            zIndex: 9999,
            boxShadow: `0 8px 24px ${alpha("#000", 0.15)}`,
          }}
          onClose={() => setCopyFeedback(null)}
        >
          {copyFeedback}
        </Alert>
      )}
    </Box>
  );
}
